import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { prospectsAPI, settingsAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { Country, State, City } from 'country-state-city';
import { CITY_SELECT_OTHER_VALUE, getCityFieldState } from '../../utils/citySelect';
import { toast } from 'react-toastify';

function defaultLanguageForCountryIso(isoCode) {
  if (!isoCode) return 'ENGLISH';
  return String(isoCode).toUpperCase() === 'FR' ? 'FRENCH' : 'ENGLISH';
}

const ProspectForm = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const config = getConfig();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [salesTeam, setSalesTeam] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [courses, setCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [phoneE164, setPhoneE164] = useState('');
  const [cityEntryMode, setCityEntryMode] = useState('list');

  const showInterestedCourse = !!config.features?.interestedCourse;

  const countries = useMemo(() => Country.getAllCountries(), []);

  const [form, setForm] = useState({
    prospect_type: 'B2C',
    name: '',
    contact_number: '',
    phone_country_code: config.defaultPhoneCode,
    email: '',
    remarks: '',
    owner_id: '',
    status: 'New',
    source: '',
    decision_maker: '',
    country: config.label,
    state: '',
    city: '',
    location_country_code: config.flagCode,
    location_state_code: '',
    title: 'Mr.',
    first_name: '',
    last_name: '',
    dob: '',
    address: '',
    zip_code: '',
    passport_no: '',
    gender: '',
    language: defaultLanguageForCountryIso(config.flagCode),
    current_degree: '',
    interested_course: ''
  });

  const statesData = useMemo(
    () => (form.location_country_code ? State.getStatesOfCountry(form.location_country_code) : []),
    [form.location_country_code]
  );
  const citiesData = useMemo(
    () => (form.location_country_code && form.location_state_code ? City.getCitiesOfState(form.location_country_code, form.location_state_code) : []),
    [form.location_country_code, form.location_state_code]
  );

  const canAssignOwner = ['super_admin', 'admin', 'manager'].includes(String(currentUser?.role || '').toLowerCase());
  const isB2B = form.prospect_type === 'B2B';

  useEffect(() => {
    const fetches = [
      usersAPI.getSalesTeam(),
      settingsAPI.getProspectStatuses(),
      settingsAPI.getSources()
    ];
    if (showInterestedCourse) fetches.push(settingsAPI.getCourses());
    if (isEdit) fetches.push(prospectsAPI.getById(id));

    Promise.allSettled(fetches).then((results) => {
      let idx = 0;
      const usersRes = results[idx++];
      const statusesRes = results[idx++];
      const sourcesRes = results[idx++];
      const coursesRes = showInterestedCourse ? results[idx++] : null;
      const prospectRes = isEdit ? results[idx++] : null;

      setSalesTeam(usersRes.status === 'fulfilled' ? (usersRes.value?.data || []) : []);
      setStatuses(statusesRes.status === 'fulfilled' ? (statusesRes.value?.data || []) : []);
      const srcRows = sourcesRes.status === 'fulfilled' ? (sourcesRes.value?.data || []) : [];
      setSources(srcRows.filter(x => x.status === 'active'));
      if (coursesRes && coursesRes.status === 'fulfilled') {
        setCourses((coursesRes.value?.data || []).filter(c => c.status === 'active'));
      }

    if (prospectRes && prospectRes.status === 'fulfilled') {
        const p = prospectRes.value.data;
        const normalizedCountryName = typeof p.country === 'string' ? p.country.trim().toLowerCase() : '';
        const normalizedStateName = typeof p.state === 'string' ? p.state.trim().toLowerCase() : '';
        const mappedCountry = p.country
          ? countries.find(c => c.name.trim().toLowerCase() === normalizedCountryName) || null
          : null;
        const nextLocationCountryCode = p.location_country_code || mappedCountry?.isoCode || config.flagCode;
        const mappedStates = nextLocationCountryCode ? State.getStatesOfCountry(nextLocationCountryCode) : [];
        const mappedState = p.state
          ? mappedStates.find(s => s.name.trim().toLowerCase() === normalizedStateName) || null
          : null;
        const cityList = nextLocationCountryCode && (p.location_state_code || mappedState?.isoCode)
          ? City.getCitiesOfState(nextLocationCountryCode, p.location_state_code || mappedState?.isoCode)
          : [];
        const { mode: loadedCityMode, cityForForm } = getCityFieldState(p.city || '', cityList);

        const cc = p.phone_country_code || config.defaultPhoneCode;
        const nat = String(p.contact_number || '');
        const ccDigits = cc.replace(/^\+/, '');
        const cleanNat = nat.startsWith(ccDigits) ? nat.slice(ccDigits.length) : nat;
        setPhoneE164(cleanNat ? `${cc}${cleanNat}` : '');

        setForm({
          prospect_type: p.prospect_type,
          name: p.name || '',
          contact_number: p.contact_number || '',
          phone_country_code: cc,
          email: p.email || '',
          remarks: p.remarks || '',
          owner_id: p.owner_id || '',
          status: p.status || 'New',
          source: p.source || '',
          decision_maker: p.decision_maker || '',
          country: p.country || config.label,
          state: p.state || '',
          city: cityForForm,
          location_country_code: nextLocationCountryCode,
          location_state_code: p.location_state_code || mappedState?.isoCode || '',
          title: p.title || 'Mr.',
          first_name: p.first_name || '',
          last_name: p.last_name || '',
          dob: p.dob ? String(p.dob).slice(0, 10) : '',
          address: p.address || '',
          zip_code: p.zip_code || '',
          passport_no: p.passport_no || '',
          gender: p.gender || '',
          language: p.language || defaultLanguageForCountryIso(nextLocationCountryCode),
          current_degree: p.current_degree || '',
          interested_course: p.interested_course || ''
        });
        setCityEntryMode(loadedCityMode);
      } else if (isEdit && prospectRes && prospectRes.status === 'rejected') {
        toast.error(t('prospects.loadFailed'));
      }
    });
  }, [id, isEdit, config.defaultPhoneCode, config.flagCode, config.label, countries, t, showInterestedCourse]);

  const sourceOptions = useMemo(() => {
    const activeSourceNames = (sources || []).map(s => s.source_name).filter(Boolean);
    if (form.source && !activeSourceNames.includes(form.source)) {
      return [form.source, ...activeSourceNames];
    }
    return activeSourceNames;
  }, [sources, form.source]);

  const statusOptions = useMemo(() => {
    const active = (statuses || [])
      .filter(s => s.status === 'active' && s.prospect_group === form.prospect_type)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(s => s.status_name);
    if (form.status && !active.includes(form.status)) active.unshift(form.status);
    return active;
  }, [statuses, form.prospect_type, form.status]);

  const splitPhoneForApi = (e164) => {
    if (!e164) return { phone_country_code: config.defaultPhoneCode, contact_number: '' };
    try {
      const parsed = parsePhoneNumber(e164);
      if (parsed?.countryCallingCode && parsed?.nationalNumber) {
        return { phone_country_code: `+${parsed.countryCallingCode}`, contact_number: parsed.nationalNumber };
      }
    } catch {}
    return { phone_country_code: config.defaultPhoneCode, contact_number: e164.replace(/^\+/, '') };
  };

  const validate = () => {
    const errs = {};
    if (isB2B) {
      if (!form.name.trim()) errs.name = t('prospects.nameRequired');
      if (!phoneE164) errs.contact_number = t('prospects.contactRequired');
      else if (!isValidPhoneNumber(phoneE164)) errs.contact_number = t('prospects.invalidPhone');
    } else {
      if (!form.name.trim()) errs.name = t('prospects.nameRequired');
      const hasPhone = phoneE164 && isValidPhoneNumber(phoneE164);
      const hasEmail = form.email && /\S+@\S+\.\S+/.test(form.email);
      if (!hasPhone && !hasEmail) errs.contact_number = t('prospects.emailOrPhoneRequired');
    }
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) errs.email = t('prospects.invalidEmail');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const phoneParts = splitPhoneForApi(phoneE164);
      const data = {
        ...form,
        contact_number: phoneParts.contact_number || null,
        phone_country_code: phoneParts.phone_country_code,
        owner_id: canAssignOwner ? (form.owner_id || null) : currentUser?.user_id
      };
      if (isEdit) {
        await prospectsAPI.update(id, data);
        toast.success(t('prospects.updateSuccess'));
        navigate(`/prospects/${id}`);
      } else {
        const res = await prospectsAPI.create(data);
        toast.success(t('prospects.createSuccess'));
        navigate(`/prospects/${res.data.prospect_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t('prospects.saveFailed'));
    }
    setLoading(false);
  };

  const handlePhoneChange = (value) => {
    setPhoneE164(value || '');
    if (errors.contact_number) setErrors(e => ({ ...e, contact_number: '' }));
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
    if (field === 'prospect_type') {
      setForm(f => ({ ...f, prospect_type: value, status: 'New' }));
    }
  };

  const handleCountrySelect = (countryCode) => {
    setCityEntryMode('list');
    const selected = countries.find(c => c.isoCode === countryCode);
    setForm(f => ({
      ...f,
      location_country_code: countryCode,
      country: selected?.name || '',
      location_state_code: '',
      state: '',
      city: '',
      language: !isB2B ? defaultLanguageForCountryIso(countryCode) : f.language
    }));
    if (errors.country) setErrors(e => ({ ...e, country: '' }));
  };

  const handleStateSelect = (stateCode) => {
    setCityEntryMode('list');
    const selected = statesData.find(s => s.isoCode === stateCode);
    setForm(f => ({
      ...f,
      location_state_code: stateCode,
      state: selected?.name || '',
      city: ''
    }));
  };

  const handleCitySelect = (cityName) => {
    setForm(f => ({ ...f, city: cityName }));
  };

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? t('prospects.editTitle') : t('prospects.addTitle')}</h1>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Prospect Type */}
            <div className="form-group">
              <label>{t('prospects.prospectType')} <span className="required">*</span></label>
              <select value={form.prospect_type} onChange={e => handleChange('prospect_type', e.target.value)} disabled={isEdit}>
                <option value="B2C">B2C</option>
                <option value="B2B">B2B</option>
              </select>
            </div>

            {/* ──── B2B FIELDS (same as LeadForm) ──── */}
            {isB2B && (
              <>
                <div className="form-group">
                  <label>{t('prospects.agencyName')} <span className="required">*</span></label>
                  <input type="text" placeholder={t('prospects.agencyNamePh')}
                    value={form.name} onChange={e => handleChange('name', e.target.value)}
                    className={errors.name ? 'error' : ''} />
                  {errors.name && <div className="error-text">{errors.name}</div>}
                </div>

                <div className="form-group">
                  <label>{t('prospects.contactNumber')} <span className="required">*</span></label>
                  <PhoneInput international defaultCountry={config.flagCode}
                    value={phoneE164 || undefined} onChange={handlePhoneChange}
                    placeholder={t('prospects.phonePlaceholder')}
                    className={errors.contact_number ? 'error' : ''} />
                  {errors.contact_number && <div className="error-text">{errors.contact_number}</div>}
                </div>

                <div className="form-group">
                  <label>{t('prospects.emailLabel')}</label>
                  <input type="email" placeholder={t('prospects.emailPlaceholder')}
                    value={form.email} onChange={e => handleChange('email', e.target.value)}
                    className={errors.email ? 'error' : ''} />
                  {errors.email && <div className="error-text">{errors.email}</div>}
                </div>

                <div className="form-group">
                  <label>{t('leadForm.source')}</label>
                  <select value={form.source} onChange={e => handleChange('source', e.target.value)}>
                    <option value="">{t('studentForm.selectSource')}</option>
                    {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('leadForm.country')}</label>
                  <select value={form.location_country_code || ''} onChange={e => handleCountrySelect(e.target.value)}>
                    <option value="">{t('common.selectCountry')}</option>
                    {countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('leadForm.state')}</label>
                  <select value={form.location_state_code || ''} onChange={e => handleStateSelect(e.target.value)}
                    disabled={!form.location_country_code || statesData.length === 0}>
                    <option value="">{statesData.length ? t('common.selectState') : t('common.selectCountryFirst')}</option>
                    {statesData.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('leadForm.city')}</label>
                  {!form.location_state_code ? (
                    <input type="text" disabled value="" placeholder={t('common.selectStateFirst')} />
                  ) : citiesData.length > 0 ? (
                    <>
                      <select
                        value={cityEntryMode === 'other' ? CITY_SELECT_OTHER_VALUE : (form.city || '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === CITY_SELECT_OTHER_VALUE) {
                            setCityEntryMode('other');
                            setForm(f => ({ ...f, city: '' }));
                          } else {
                            setCityEntryMode('list');
                            handleCitySelect(v);
                          }
                        }}>
                        <option value="">{t('common.selectCity')}</option>
                        {citiesData.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                        <option value={CITY_SELECT_OTHER_VALUE}>{t('common.otherEnterCity')}</option>
                      </select>
                      {cityEntryMode === 'other' && (
                        <input type="text" value={form.city || ''} onChange={e => handleCitySelect(e.target.value)}
                          placeholder={t('common.typeCityName')} style={{ marginTop: 8 }} />
                      )}
                    </>
                  ) : (
                    <input value={form.city || ''} onChange={e => handleCitySelect(e.target.value)} placeholder={t('common.enterCity')} />
                  )}
                </div>

                <div className="form-group">
                  <label>{t('leadForm.contactPerson')}</label>
                  <input type="text" placeholder={t('leadForm.contactPersonPlaceholder')}
                    value={form.decision_maker} onChange={e => handleChange('decision_maker', e.target.value)} />
                </div>
              </>
            )}

            {/* ──── B2C FIELDS (same as StudentForm) ──── */}
            {!isB2B && (
              <>
                <div className="form-group">
                  <label>{t('studentForm.titleLabel')}</label>
                  <select value={form.title} onChange={e => handleChange('title', e.target.value)}>
                    {[{ v: 'Mr.', l: t('studentForm.mr') }, { v: 'Ms.', l: t('studentForm.ms') }, { v: 'Mrs.', l: t('studentForm.mrs') }, { v: 'Dr.', l: t('studentForm.dr') }].map(o =>
                      <option key={o.v} value={o.v}>{o.l}</option>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('prospects.personName')} <span className="required">*</span></label>
                  <input type="text" placeholder={t('prospects.personNamePh')}
                    value={form.name} onChange={e => handleChange('name', e.target.value)}
                    className={errors.name ? 'error' : ''} />
                  {errors.name && <div className="error-text">{errors.name}</div>}
                </div>

                <div className="form-group">
                  <label>{t('studentForm.dob')}</label>
                  <input type="date" value={form.dob} onChange={e => handleChange('dob', e.target.value)} />
                </div>

                <div className="form-group">
                  <label>{t('prospects.contactNumber')}</label>
                  <PhoneInput international defaultCountry={config.flagCode}
                    value={phoneE164 || undefined} onChange={handlePhoneChange}
                    placeholder={t('prospects.phonePlaceholder')}
                    className={errors.contact_number ? 'error' : ''} />
                  {errors.contact_number && <div className="error-text">{errors.contact_number}</div>}
                </div>

                <div className="form-group">
                  <label>{t('prospects.emailLabel')}</label>
                  <input type="email" placeholder={t('prospects.emailPlaceholder')}
                    value={form.email} onChange={e => handleChange('email', e.target.value)}
                    className={errors.email ? 'error' : ''} />
                  {errors.email && <div className="error-text">{errors.email}</div>}
                </div>

                <div className="form-group">
                  <label>{t('studentForm.passportNumber')}</label>
                  <input value={form.passport_no} onChange={e => handleChange('passport_no', e.target.value)}
                    placeholder={t('studentForm.passportPlaceholder')} />
                </div>

                <div className="form-group">
                  <label>{t('common.gender')}</label>
                  <select value={form.gender} onChange={e => handleChange('gender', e.target.value)}>
                    <option value="">{t('studentForm.selectGender')}</option>
                    <option value="Male">{t('common.male')}</option>
                    <option value="Female">{t('common.female')}</option>
                    <option value="Other">{t('common.other')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('studentForm.languageLabel')}</label>
                  <select value={form.language} onChange={e => handleChange('language', e.target.value)}>
                    <option value="ENGLISH">{t('studentForm.languageEnglish')}</option>
                    <option value="FRENCH">{t('studentForm.languageFrench')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('studentForm.lastDegree')}</label>
                  <input value={form.current_degree} onChange={e => handleChange('current_degree', e.target.value)} />
                </div>

                {showInterestedCourse && (
                  <div className="form-group">
                    <label>{t('prospects.interestedCourse')}</label>
                    <select value={form.interested_course} onChange={e => handleChange('interested_course', e.target.value)}>
                      <option value="">{t('prospects.selectCourse')}</option>
                      {courses.map(c => <option key={c.course_id} value={c.course_name}>{c.course_name}</option>)}
                    </select>
                  </div>
                )}

                <div className="form-group full-width">
                  <label>{t('common.address')}</label>
                  <input value={form.address} onChange={e => handleChange('address', e.target.value)} placeholder={t('common.streetAddress')} />
                </div>

                <div className="form-group">
                  <label>{t('studentForm.country')}</label>
                  <select value={form.location_country_code || ''} onChange={e => handleCountrySelect(e.target.value)}>
                    <option value="">{t('common.selectCountry')}</option>
                    {countries.map(c => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('studentForm.state')}</label>
                  <select value={form.location_state_code || ''} onChange={e => handleStateSelect(e.target.value)}
                    disabled={!form.location_country_code || statesData.length === 0}>
                    <option value="">{statesData.length ? t('common.selectState') : t('common.selectCountryFirst')}</option>
                    {statesData.map(s => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('studentForm.city')}</label>
                  {!form.location_state_code ? (
                    <input type="text" disabled value="" placeholder={t('common.selectStateFirst')} />
                  ) : citiesData.length > 0 ? (
                    <>
                      <select
                        value={cityEntryMode === 'other' ? CITY_SELECT_OTHER_VALUE : (form.city || '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === CITY_SELECT_OTHER_VALUE) {
                            setCityEntryMode('other');
                            setForm(f => ({ ...f, city: '' }));
                          } else {
                            setCityEntryMode('list');
                            handleCitySelect(v);
                          }
                        }}>
                        <option value="">{t('common.selectCity')}</option>
                        {citiesData.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                        <option value={CITY_SELECT_OTHER_VALUE}>{t('common.otherEnterCity')}</option>
                      </select>
                      {cityEntryMode === 'other' && (
                        <input type="text" value={form.city || ''} onChange={e => handleCitySelect(e.target.value)}
                          placeholder={t('common.typeCityName')} style={{ marginTop: 8 }} />
                      )}
                    </>
                  ) : (
                    <input value={form.city || ''} onChange={e => handleCitySelect(e.target.value)} placeholder={t('common.enterCity')} />
                  )}
                </div>

                <div className="form-group">
                  <label>{t('common.zipCode')}</label>
                  <input value={form.zip_code} onChange={e => handleChange('zip_code', e.target.value)} />
                </div>

                <div className="form-group">
                  <label>{t('studentForm.source')}</label>
                  <select value={form.source || ''} onChange={e => handleChange('source', e.target.value)}>
                    <option value="">{t('studentForm.selectSource')}</option>
                    {sourceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </>
            )}

            {/* ──── COMMON FIELDS ──── */}
            {isEdit && (
              <div className="form-group">
                <label>{t('prospects.statusLabel')}</label>
                <select value={form.status} onChange={e => handleChange('status', e.target.value)}>
                  {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            {canAssignOwner && (
              <div className="form-group">
                <label>{t('prospects.owner')}</label>
                <select value={form.owner_id} onChange={e => handleChange('owner_id', e.target.value)}>
                  <option value="">{t('prospects.defaultOwner')}</option>
                  {salesTeam.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
              </div>
            )}
            {!canAssignOwner && (
              <div className="form-group">
                <label>{t('prospects.owner')}</label>
                <input type="text" value={currentUser?.name || 'Me'} disabled style={{ background: '#f5f5f5' }} />
              </div>
            )}

            <div className="form-group full-width">
              <label>{t('prospects.remarks')}</label>
              <textarea placeholder={t('prospects.remarksPlaceholder')} value={form.remarks}
                onChange={e => handleChange('remarks', e.target.value)} maxLength={1000} />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/prospects')}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.saving') : (isEdit ? t('common.update') : t('common.save'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProspectForm;
